<template>
    <div>
        <h5 class="fw-bold mb-1">Powiązane konta użytkowników</h5>
        <p class="text-muted small mb-4">Zarządzaj dostępami do kont, przełączaj się między profilami oraz akceptuj
            zaproszenia od innych użytkowników.</p>

        <!-- SEKCJA 1: KONTA, DO KTÓRYCH MAMY DOSTĘP (WYSŁANE ZAPROSZENIA) -->
        <div class="card border mb-4 shadow-sm">
            <div class="card-header bg-light d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 py-3">
                <h6 class="fw-bold mb-0">Konta, na które możesz się przełączyć</h6>
                <button type="button" class="btn btn-primary-dark btn-sm d-flex align-items-center gap-2"
                    @click="openInviteModal" :disabled="saving">
                    <i class="bi bi-person-plus"></i>
                    <span>Wyślij zaproszenie</span>
                </button>
            </div>
            <div class="card-body p-0">
                <Vue3EasyDataTable :headers="outgoingHeaders" :items="outgoingInvites" :loading="loadingOutgoing"
                    buttons-pagination show-index empty-message="Brak powiązanych kont lub wysłanych zaproszeń.">
                    <!-- E-MAIL -->
                    <template #item-email="{ email }">
                        <span class="fw-semibold">{{ email }}</span>
                    </template>

                    <!-- STATUS -->
                    <template #item-status="{ status }">
                        <span v-if="status === 'pending'"
                            class="badge bg-warning-subtle text-warning border border-warning-subtle">
                            <i class="bi bi-clock me-1"></i>Oczekuje na akceptację
                        </span>
                        <span v-else-if="status === 'accepted'"
                            class="badge bg-success-subtle text-success border border-success-subtle">
                            <i class="bi bi-check-circle me-1"></i>Aktywne
                        </span>
                    </template>

                    <!-- LOGOWANIE BEZ HASŁA -->
                    <template #item-allowPasswordless="{ status, allowPasswordless }">
                        <span v-if="status === 'accepted'">
                            <i v-if="allowPasswordless === 'yes' || allowPasswordless === true"
                                class="bi bi-shield-check text-success fs-5"
                                title="Zezwolono na logowanie bez hasła"></i>
                            <i v-else class="bi bi-shield-lock text-muted fs-5" title="Wymagane podanie hasła"></i>
                        </span>
                        <span v-else class="text-muted small">-</span>
                    </template>

                    <!-- AKCJE -->
                    <template #item-actions="item">
                        <div class="d-flex justify-content-end gap-1 py-1">
                            <!-- Przycisk zaloguj dla zaakceptowanych -->
                            <button v-if="item.status === 'accepted'"
                                class="btn btn-sm btn-primary d-flex align-items-center gap-1"
                                title="Zaloguj na to konto" @click="handleLoginAttempt(item)">
                                <i class="bi bi-box-arrow-in-right"></i>
                                <span>Zaloguj</span>
                            </button>
                            <!-- Przycisk usuń/anuluj -->
                            <button class="btn btn-sm btn-outline-danger"
                                :title="item.status === 'pending' ? 'Anuluj zaproszenie' : 'Usuń powiązanie'"
                                @click="removeInvite(item.ID)">
                                <i class="bi bi-trash"></i>
                            </button>
                        </div>
                    </template>
                </Vue3EasyDataTable>
            </div>
        </div>

        <!-- SEKCJA 2: OTRZYMANE ZAPROSZENIA I DOSTĘPY DO MOJEGO KONTA -->
        <div class="card border shadow-sm">
            <div class="card-header bg-light py-3">
                <h6 class="fw-bold mb-0">Użytkownicy posiadający dostęp do Twojego konta</h6>
            </div>
            <div class="card-body p-0">
                <Vue3EasyDataTable :headers="incomingHeaders" :items="incomingInvites" :loading="loadingIncoming"
                    buttons-pagination show-index empty-message="Brak odebranych zaproszeń i udzielonych dostępów.">
                    <!-- E-MAIL -->
                    <template #item-email="{ email }">
                        <span class="fw-semibold">{{ email }}</span>
                    </template>

                    <!-- STATUS / ZGODA BEZHASŁOWA -->
                    <template #item-status="item">
                        <div v-if="item.status === 'pending'">
                            <span class="badge bg-info-subtle text-info border border-info-subtle">Zaproszenie oczekuje
                                na Twoją decyzję</span>
                        </div>
                        <div v-else-if="item.status === 'accepted'">
                            <!-- Pełny zielony przycisk dla aktywnego braku hasła -->
                            <button v-if="item.allowPasswordless === 'yes' || item.allowPasswordless === true"
                                class="btn btn-sm btn-success  d-inline-flex align-items-center gap-1 py-1 px-2 shadow-sm fw-medium"
                                title="Kliknij, aby wymagać hasła przy logowaniu"
                                @click="confirmTogglePasswordless(item)">
                                <i class="bi bi-check-circle-fill"></i>
                                <span>Bez hasła</span>
                            </button>

                            <!-- Pełny czerwony przycisk dla wymogu hasła -->
                            <button v-else
                                class="btn btn-sm btn-danger  d-inline-flex align-items-center gap-1 py-1 px-2 shadow-sm fw-medium"
                                title="Kliknij, aby zezwolić na logowanie bez hasła"
                                @click="confirmTogglePasswordless(item)">
                                <i class="bi bi-shield-lock-fill"></i>
                                <span>Wymaga hasła</span>
                            </button>
                        </div>
                    </template>

                    <!-- AKCJE -->
                    <template #item-actions="item">
                        <div v-if="item.status === 'pending'" class="d-flex justify-content-end gap-1 py-1">
                            <button class="btn btn-sm btn-success d-flex align-items-center gap-1"
                                @click="openAcceptModal(item)">
                                <i class="bi bi-check-lg"></i>
                                <span>Zatwierdź</span>
                            </button>
                            <button class="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                @click="rejectInvite(item.ID)">
                                <i class="bi bi-x-lg"></i>
                                <span>Odrzuć</span>
                            </button>
                        </div>
                        <div v-else class="d-flex justify-content-end py-1">
                            <button class="btn btn-sm btn-outline-danger" title="Cofnij dostęp"
                                @click="removeInvite(item.ID)">
                                <i class="bi bi-trash"></i>
                                <span>Cofnij dostęp</span>
                            </button>
                        </div>
                    </template>
                </Vue3EasyDataTable>
            </div>
        </div>

        <!-- MODAL 1: WYSYŁANIE ZAPROSZENIA -->
        <div ref="inviteModalRef" class="modal fade" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title fw-bold">Wyślij zaproszenie</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <form @submit.prevent="sendInvite">
                        <div class="modal-body">
                            <div class="mb-3">
                                <label class="form-label fw-semibold">Adres e-mail użytkownika</label>
                                <input type="email" class="form-control" v-model="inviteEmail" required
                                    placeholder="np. jan.kowalski@example.com" :disabled="saving" />
                                <div class="form-text">Na ten adres zostanie wysłana prośba o powiązanie kont.</div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Anuluj</button>
                            <button type="submit" class="btn btn-primary-dark" :disabled="saving">Wyślij
                                zaproszenie</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- MODAL 2: LOGOWANIE Z HASŁEM -->
        <div ref="passwordModalRef" class="modal fade" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title fw-bold">Wymagana autoryzacja</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <form @submit.prevent="executeLoginWithPassword">
                        <div class="modal-body">
                            <p class="small text-muted mb-3">Użytkownik <strong>{{ selectedTargetUser?.email }}</strong>
                                wymaga
                                podania hasła przy logowaniu na jego konto.</p>
                            <div class="mb-3">
                                <label class="form-label fw-semibold">Hasło do konta docelowego</label>
                                <div class="input-group">
                                    <input :type="showTargetPassword ? 'text' : 'password'" class="form-control"
                                        v-model="targetUserPassword" required :disabled="saving" />
                                    <button class="btn btn-outline-secondary" type="button"
                                        @click="showTargetPassword = !showTargetPassword">
                                        <i class="bi" :class="showTargetPassword ? 'bi-eye-slash' : 'bi-eye'"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Anuluj</button>
                            <button type="submit" class="btn btn-primary-dark" :disabled="saving">Zaloguj się</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- MODAL 3: AKCEPTACJA ZAPROSZENIA -->
        <div ref="acceptModalRef" class="modal fade" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title fw-bold">Zatwierdź zaproszenie</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p class="mb-3">Czy chcesz przyznać użytkownikowi <strong>{{ selectedIncomingInvite?.email
                        }}</strong>
                            dostęp do Twojego konta?</p>

                        <div class="p-3 rounded border transition-all" :class="{
                            'bg-light border-secondary-subtle text-secondary': acceptAllowPasswordless === null,
                            'bg-success-subtle border-success text-success-emphasis': acceptAllowPasswordless === 'yes',
                            'bg-warning-subtle border-warning text-warning-emphasis': acceptAllowPasswordless === 'no'
                        }">
                            <label class="fw-semibold d-block mb-2">
                                Przelogowywanie się na Twoje konto bez podawania hasła:
                            </label>

                            <div class="d-flex align-items-center gap-2">
                                <button type="button"
                                    class="btn btn-sm flex-fill d-flex align-items-center justify-content-center gap-1"
                                    :class="acceptAllowPasswordless === 'yes' ? 'btn-success fw-bold' : 'btn-outline-success bg-white'"
                                    @click="acceptAllowPasswordless = 'yes'">
                                    <i class="bi bi-check-circle-fill" v-if="acceptAllowPasswordless === 'yes'"></i>
                                    <span>TAK (Pozwól bez hasła)</span>
                                </button>

                                <button type="button"
                                    class="btn btn-sm flex-fill d-flex align-items-center justify-content-center gap-1"
                                    :class="acceptAllowPasswordless === 'no' ? 'btn-warning ' : 'btn-outline-warning '"
                                    @click="acceptAllowPasswordless = 'no'">
                                    <i class="bi bi-shield-lock-fill" v-if="acceptAllowPasswordless === 'no'"></i>
                                    <span>NIE (Wymagaj hasła)</span>
                                </button>
                            </div>

                            <div class="small mt-2">
                                <span v-if="acceptAllowPasswordless === null" class="text-muted">
                                    <i class="bi bi-info-circle me-1"></i>Wybierz jedną z opcji, aby przejść dalej.
                                </span>
                                <span v-else-if="acceptAllowPasswordless === 'yes'">
                                    <i class="bi bi-shield-check me-1"></i>Użytkownik będzie mógł przełączyć się na
                                    Twoje konto
                                    jednym kliknięciem.
                                </span>
                                <span v-else-if="acceptAllowPasswordless === 'no'">
                                    <i class="bi bi-shield-lock me-1"></i>Użytkownik będzie musiał podać Twoje hasło
                                    przy każdej
                                    próbie przełączenia.
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-light" data-bs-dismiss="modal">Anuluj</button>
                        <button type="button" class="btn btn-success"
                            :disabled="saving || acceptAllowPasswordless === null" @click="confirmAcceptInvite">
                            Zatwierdzam
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- LOADER AKCJI -->
        <AppLoader :show="saving" :text="loaderText" />
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import apiClient from '@/api'
import Modal from 'bootstrap/js/dist/modal'
import Vue3EasyDataTable from 'vue3-easy-data-table'
import 'vue3-easy-data-table/dist/style.css'
import AppLoader from '@/components/AppLoader.vue'
import { webAlert, showConfirm } from '@/utils/dialog'


const saving = ref(false)
const loadingOutgoing = ref(false)
const loadingIncoming = ref(false)
const loaderText = ref('')

const outgoingInvites = ref([])
const incomingInvites = ref([])

// Konfiguracja nagłówków Vue3EasyDataTable
const outgoingHeaders = [
    { text: 'Adres e-mail', value: 'email', sortable: true },
    { text: 'Status', value: 'status', sortable: true },
    { text: 'Logowanie bez hasła', value: 'allowPasswordless' },
    { text: '', value: 'actions', width: 180 }
]

const incomingHeaders = [
    { text: 'Adres e-mail', value: 'email', sortable: true },
    { text: 'Status / Zgoda bezhasłowa', value: 'status', sortable: true },
    { text: '', value: 'actions', width: 220 }
]

// Referencje Modali
const inviteModalRef = ref(null)
const passwordModalRef = ref(null)
const acceptModalRef = ref(null)

let inviteModalInstance = null
let passwordModalInstance = null
let acceptModalInstance = null

// Formularze Modali
const inviteEmail = ref('')
const targetUserPassword = ref('')
const showTargetPassword = ref(false)
const selectedTargetUser = ref(null)
const selectedIncomingInvite = ref(null)
const acceptAllowPasswordless = ref(null)

// Mapowanie obiektów
const mapInviteItem = (item) => ({
    ...item,
    ID: item.ID || item.id,
    email: item.email || item.targetEmail || '',
    allowPasswordless: (item.allowPasswordless === 'yes' || item.allowPasswordless === true || item.allowPasswordless === '1') ? 'yes' : 'no'
})

// Dedykowana metoda do asynchronicznego pobierania danych
const loadData = async () => {
    loadingOutgoing.value = true
    loadingIncoming.value = true

    try {
        const response = await apiClient.post({ action: 'get-linked-accounts' })

        const rawOutgoing = response.data?.data?.outgoing || response.data?.data?.outgoind || []
        const rawIncoming = response.data?.data?.incoming || []

        outgoingInvites.value = rawOutgoing.map(mapInviteItem)
        incomingInvites.value = rawIncoming.map(mapInviteItem)

    } catch (error) {
        webAlert('Błąd podczas pobierania powiązanych kont.')
    } finally {
        loadingOutgoing.value = false
        loadingIncoming.value = false
    }
}

// 1. Otwieranie Modala Wysyłania
const openInviteModal = () => {
    inviteEmail.value = ''
    inviteModalInstance.show()
}

// Wysyłanie Zaproszenia
const sendInvite = async () => {
    if (!inviteEmail.value) return

    saving.value = true
    loaderText.value = 'Wysyłanie zaproszenia...'

    try {
        const response = await apiClient.post(
            {
                action: 'send-account-invite',
                email: inviteEmail.value
            }
        )

        if (response.data?.status === 'success') {
            inviteModalInstance.hide()
            loadData()
        }

        webAlert(response.data);

    } catch (error) {
        webAlert('Błąd podczas wysyłania zaproszenia.')
    } finally {
        saving.value = false
    }
}

// 2. Próba logowania
const handleLoginAttempt = (targetUser) => {
    selectedTargetUser.value = targetUser
    if (targetUser.allowPasswordless === 'yes' || targetUser.allowPasswordless === true) {
        switchAccount(targetUser.ID || targetUser.id, null)
    } else {
        targetUserPassword.value = ''
        showTargetPassword.value = false
        passwordModalInstance.show()
    }
}

const executeLoginWithPassword = () => {
    if (!targetUserPassword.value) return
    passwordModalInstance.hide()
    switchAccount(selectedTargetUser.value.ID || selectedTargetUser.value.id, targetUserPassword.value)
}

const switchAccount = async (targetUserId, password = null) => {
    saving.value = true
    loaderText.value = 'Przełączanie konta...'

    try {
        const response = await apiClient.post(
            {
                action: 'switch-account',
                targetUserId: targetUserId,
                password: password
            }
        )

        if (response.data?.token) {
            localStorage.setItem('integrator_token', response.data.token)
            if (response.data?.user) {
                localStorage.setItem('user_role', response.data.user.role)
                localStorage.setItem('user_email', response.data.user.email)
            }

            //console.log(response.data);
            setTimeout(() => { window.location.reload(); }, 500);
        } else {
            webAlert('Nie otrzymano nowego tokena autoryzacji.')
        }

    } catch (error) {
        saving.value = false
        webAlert('Nie udało się przełączyć konta. Sprawdź poprawność hasła lub uprawnienia.');
    }
}

// 3. Akceptacja Zaproszenia
const openAcceptModal = (item) => {
    selectedIncomingInvite.value = item
    acceptAllowPasswordless.value = null
    acceptModalInstance.show()
}

const confirmAcceptInvite = async () => {
    if (!acceptAllowPasswordless.value) return

    acceptModalInstance.hide()
    saving.value = true
    loaderText.value = 'Zatwierdzanie zaproszenia...'

    try {
        const response = await apiClient.post(
            {
                action: 'respond-account-invite',
                inviteId: selectedIncomingInvite.value.ID,
                accept: true,
                allowPasswordless: acceptAllowPasswordless.value
            }
        )

        loadData()

        webAlert(response.data);

    } catch (error) {
        webAlert('Błąd podczas zatwierdzania zaproszenia.')
    } finally {
        saving.value = false
    }
}

// 4. Odrzucenie Zaproszenia
const rejectInvite = async (inviteId) => {
    const confirmed = await showConfirm('Czy na pewno chcesz odrzucić to zaproszenie?', {
        title: 'Odrzucenie zaproszenia',
        confirmText: 'Odrzuć',
        cancelText: 'Anuluj',
        confirmClass: 'btn-danger',
        icon: 'bi-x-circle text-danger'
    })

    if (!confirmed) return

    saving.value = true
    loaderText.value = 'Odrzucanie zaproszenia...'

    try {
        await apiClient.post(
            {
                action: 'respond-account-invite',
                inviteId: inviteId,
                accept: false
            }
        )

        loadData()
        webAlert({ status: 'success', message: 'Zaproszenie zostało odrzucone.' });

    } catch (error) {
        webAlert('Błąd podczas odrzucania zaproszenia.')
    } finally {
        saving.value = false
    }
}

// 5. Potwierdzenie i zmiana zgody na logowanie bezhasłowe
const confirmTogglePasswordless = async (item) => {
    const isCurrentlyYes = (item.allowPasswordless === 'yes' || item.allowPasswordless === true)
    const nextState = isCurrentlyYes ? 'no' : 'yes'

    const title = isCurrentlyYes ? 'Wymagaj podawania hasła' : 'Zezwól na logowanie bez hasła'
    const message = isCurrentlyYes
        ? `Czy chcesz zmienić zasady dostępu dla użytkownika <strong>${item.email}</strong>?<br><br>Użytkownik będzie musiał podać Twoje hasło przy każdej próbie przełączenia się na Twoje konto.`
        : `Czy na pewno chcesz zezwolić użytkownikowi <strong>${item.email}</strong> na przelogowywanie się na Twoje konto bez podawania hasła?`

    const confirmed = await showConfirm(message, {
        title: title,
        confirmText: isCurrentlyYes ? 'Wymagaj hasła' : 'Zezwól bez hasła',
        cancelText: 'Anuluj',
        confirmClass: isCurrentlyYes ? 'btn-danger' : 'btn-success',
        icon: isCurrentlyYes ? 'bi-shield-lock text-danger' : 'bi-shield-check text-success'
    })

    if (!confirmed) return

    togglePasswordlessAccess(item, nextState)
}

const togglePasswordlessAccess = async (item, newStatus) => {
    saving.value = true
    loaderText.value = 'Aktualizacja uprawnień...'

    try {
        await apiClient.post(
            {
                action: 'toggle-passwordless-access',
                inviteId: item.ID,
                allowPasswordless: newStatus
            }
        )

        // Przeładowujemy tabele z serwera, aby widok od razu odzwierciedlał zapisany stan
        await loadData()

    } catch (error) {
        webAlert('Błąd podczas zmiany zgody.')
    } finally {
        saving.value = false
    }
}

// 6. Usuwanie / Anulowanie Powiązania
const removeInvite = async (inviteId) => {
    const confirmed = await showConfirm('Czy na pewno chcesz usunąć to powiązanie/zaproszenie?', {
        title: 'Usuwanie powiązania',
        confirmText: 'Usuń',
        cancelText: 'Anuluj',
        confirmClass: 'btn-danger',
        icon: 'bi-trash text-danger'
    })

    if (!confirmed) return

    saving.value = true
    loaderText.value = 'Usuwanie...'

    try {
        const response = await apiClient.post({ action: 'delete-account-invite', inviteId: inviteId })

        loadData()
        webAlert(response.data)

    } catch (error) {
        webAlert('Błąd podczas usuwania powiązania.')
    } finally {
        saving.value = false
    }
}

onMounted(() => {
    inviteModalInstance = new Modal(inviteModalRef.value)
    passwordModalInstance = new Modal(passwordModalRef.value)
    acceptModalInstance = new Modal(acceptModalRef.value)

    loadData()
})
</script>

<style scoped>
.transition-all {
    transition: all 0.25s ease-in-out;
}

:deep(.vue3-easy-data-table__main) {
    border: none;
}
</style>